import { KEY_CODES } from '../utils/constants';

/**
 * TrapFocus class to manage focus within a specified element.
 * When activated, it traps the focus within the element,
 * cycling through focusable elements with Tab and Shift+Tab,
 * and allows closing with the Escape key.
 *
 * @param {HTMLElement} el - The element to trap focus within.
 * @param {Function} handleEscape - Callback function to handle Escape key press.
 * @returns {void}
 */
class TrapFocus {
  constructor(el, handleEscape) {
    this.handleEscape = handleEscape;
    this.focusableElsString = `
      a[href],
      area[href],
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      button:not([disabled]),
      iframe,
      object,
      embed,
      [tabindex]:not([tabindex="-1"])
    `;

    this.focusableEls = el.querySelectorAll(this.focusableElsString);
    this.firstFocusableEl = this.focusableEls[0];
    this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];

    this.handleKeyDown = this.keydown.bind(this);
  }

  /**
   * Activate the trap focus functionality by adding keydown event listener.
   *
   * @returns {void}
   */
  activate() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Deactivate the trap focus functionality by removing keydown event listener.
   *
   * @returns {void}
   */
  deactivate() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Handle back tab event by focusing the last focusable element if the first is active.
   *
   * @returns {void}
   */
  handleBackwardTab(e) {
    if (document.activeElement === this.firstFocusableEl) {
      e.preventDefault();
      this.lastFocusableEl.focus();
    }
  }

  /**
   * Handle forward tab event by focusing the first focusable element if the last is active.
   *
   * @returns {void}
   */
  handleForwardTab(e) {
    if (document.activeElement === this.lastFocusableEl) {
      e.preventDefault();
      this.firstFocusableEl.focus();
    }
  }

  /**
   * Handle keydown events for trapping focus and closing a menu.
   *
   * @param {KeyboardEvent} e - The keyboard event object.
   * @returns {void}
   */
  keydown(e) {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11: {
        this.handleEscape();
        break;
      }

      case KEY_CODES.TAB: {
        if (this.focusableEls.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          this.handleBackwardTab(e);
        } else {
          this.handleForwardTab(e);
        }
        break;
      }

      default: {
        break;
      }
    }
  }
}

export default TrapFocus;
